import { useEffect, useRef, useState } from "react";

import {
    Play,
    Pause,
    Volume2,
    Loader2,
} from "lucide-react";

import { Slider } from "@/components/ui/slider";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { TANPURA_KEYS } from "@/lib/notes";

import {
    startTanpura,
    stopSource,
    masterGain,
    preloadTanpura,
} from "@/lib/audio";

export default function Tanpura() {

    const [keyIdx, setKeyIdx] = useState(7);

    const [playing, setPlaying] = useState(false);

    const [volume, setVolume] = useState(0.85);

    const [loading, setLoading] = useState(false);

    const [ready, setReady] = useState(false);

    const sourceRef = useRef(null);

    const requestIdRef = useRef(0);

    ////////////////////////////////////////////////////////
    // Read Scale from URL
    ////////////////////////////////////////////////////////

    useEffect(() => {

        const params = new URLSearchParams(
            window.location.search
        );

        const scale = params.get("scale");

        if (!scale) return;

        const cleanScale = decodeURIComponent(scale)
            .trim()
            .toUpperCase();

        const index = TANPURA_KEYS.findIndex(
            k => k.note.toUpperCase() === cleanScale
        );

        if (index >= 0) {

            setKeyIdx(index);

            setPlaying(true);

        }

    }, []);

    ////////////////////////////////////////////////////////
    // Preload Tanpura
    ////////////////////////////////////////////////////////

    useEffect(() => {

        preloadTanpura()

            .then(() => setReady(true))

            .catch(() => setReady(true));

    }, []);

    ////////////////////////////////////////////////////////
    // Volume
    ////////////////////////////////////////////////////////

    useEffect(() => {

        const gain = masterGain();

        if (gain) {

            gain.gain.value = volume;

        }

    }, [volume]);

    ////////////////////////////////////////////////////////
    // Start / Stop
    ////////////////////////////////////////////////////////

    useEffect(() => {

        const reqId = ++requestIdRef.current;

        if (!playing) {

            if (sourceRef.current) {

                stopSource(sourceRef.current);

                sourceRef.current = null;

            }

            setLoading(false);

            return;

        }

        if (sourceRef.current) {

            stopSource(sourceRef.current);

            sourceRef.current = null;

        }

        setLoading(true);

        const selectedKey = TANPURA_KEYS[keyIdx];

        startTanpura(selectedKey.note, 1.0)

            .then((src) => {

                if (requestIdRef.current !== reqId) {

                    stopSource(src);

                    return;

                }

                sourceRef.current = src;

                setLoading(false);

            })

            .catch(() => {

                setLoading(false);

            });

        return () => {

            if (sourceRef.current) {

                stopSource(sourceRef.current);

                sourceRef.current = null;

            }

        };

    }, [playing, keyIdx]);

    ////////////////////////////////////////////////////////
    // JSX Starts Here
    ////////////////////////////////////////////////////////

    return (
	
	<div
    data-testid="tanpura-page"
    className="space-y-10"
>

    <header>

        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Tool · 01
        </div>

        <h1 className="font-sans font-extrabold text-5xl lg:text-6xl tracking-tight">
            Tanpura
        </h1>

        <p className="text-muted-foreground mt-3 max-w-2xl">
            A real recorded tanpura drone
            (Sa–Pa–Sa–Sa cycle),
            pitch-shifted live to any key.
        </p>

    </header>

    <div className="grid lg:grid-cols-3 gap-6">

        {/* LEFT PANEL */}

        <div className="lg:col-span-2 rounded-lg border border-border bg-card p-8">

            <div className="mb-6">

                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                    Select Scale
                </div>

                <Select
                    value={String(keyIdx)}
                    onValueChange={(value) => {

                        setKeyIdx(Number(value));

                    }}
                >

                    <SelectTrigger className="w-full h-14 text-lg">

                        <SelectValue />

                    </SelectTrigger>

                    <SelectContent>

                        {TANPURA_KEYS.map((key, index) => (

                            <SelectItem
                                key={key.note}
                                value={String(index)}
                            >

                                {key.label}

                            </SelectItem>

                        ))}

                    </SelectContent>

                </Select>

            </div>


        </div>

        {/* RIGHT PANEL */}

        <div className="rounded-lg border border-border bg-card p-8 flex flex-col gap-7">

            <div>

                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">

                    Now Playing

                </div>

                <div className="font-bold text-5xl">

                    {TANPURA_KEYS[keyIdx].note}

                </div>

                <div className="font-mono text-xs text-muted-foreground mt-2">

                    {TANPURA_KEYS[keyIdx].label}

                </div>

            </div>

            <button

                onClick={() => setPlaying(!playing)}

                disabled={loading && !ready}

                className={`

                    h-14

                    rounded-md

                    font-medium

                    flex

                    items-center

                    justify-center

                    gap-2

                    transition-all

                    ${playing

                        ? "bg-foreground text-background"

                        : "bg-primary text-primary-foreground"

                    }

                `}

            >

                {loading ? (

                    <>

                        <Loader2
                            size={18}
                            className="animate-spin"
                        />

                        Loading...

                    </>

                ) : playing ? (

                    <>

                        <Pause size={18} />

                        Stop

                    </>

                ) : (

                    <>

                        <Play size={18} />

                        Play Tanpura

                    </>

                )}

            </button>
			
			            {/* Volume */}

            <div>

                <div className="flex items-center justify-between mb-2">

                    <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">

                        <Volume2 size={14} />

                        Volume

                    </label>

                    <span className="font-mono text-xs text-muted-foreground">

                        {Math.round(volume * 100)}%

                    </span>

                </div>

                <Slider
                    value={[volume]}
                    onValueChange={([v]) => setVolume(v)}
                    min={0}
                    max={1}
                    step={0.01}
                />

            </div>



        </div>

    </div>

</div>

);

}